// Froala
import NvFroala from 'froala/components/nv-froala';
import { SanitizationLevel } from 'froala/helpers/sanitizer';
import { FroalaViewMode, NvFroalaProps, UploadType } from 'froala/helpers/nv-froala-constants';

// Styles
import { css, SerializedStyles } from '@emotion/react';
import { danger, gray3, white } from 'styles/global_defaults/colors';
import { novoEdBlue } from 'athena/styles/colors';
import { omit } from 'underscore';
import { getSanitizedStyles } from 'shared/utils';
import NvPopover from 'shared/components/nv-popover';
import { useFormContext } from 'react-hook-form';
import { useState } from 'react';
import t from 'react-translate';
import { largeSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';
import { textMediumFontSize } from 'styles/global_defaults/fonts';

export enum ResizeType {
  NONE = '',
  BOTH = 'both',
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

const FROALA_MAX_HEIGHT = 900;

const defaultConfig = {
  withForm: true,
  allowToolbar: true,
  className: 'w-100',
  uploadType: UploadType.NONE,
  preset: FroalaViewMode.NORMAL,
  sanitizationLevel: SanitizationLevel.BASIC,
};

const ErrorPopoverContent = ({ message = '' }) => (
  <div>
    <div className='text-danger text-center semi-bold mb-2'>
      {t.VALIDATION.ERROR()}
    </div>
    <div>{message}</div>
  </div>
);

const styles = css`
  .fr-wrapper {
    border: 1px solid ${gray3};
    border-radius: ${quarterSpacing}px;
    box-shadow: none;
    background-color: transparent;
  }

  .fr-element {
    background-color: transparent !important;
    resize: vertical;

    &:hover,
    &:focus {
      border-radius: ${quarterSpacing}px;
    }

    &:focus {
      outline: 2px solid ${novoEdBlue} !important;
      outline-offset: -1px !important;
      box-shadow: none;
    }
  }

  .fr-placeholder {
    background-color: ${white} !important;
    border: none !important;
    border-radius: ${quarterSpacing}px;
  }
`;

type NvAthenaFroalaProps = NvFroalaProps & {
  containerClassName?: string;
  maxHeight?: number;
  resize?: ResizeType;
  overlayStyles?: SerializedStyles;
  required?: boolean;
};

const NvAthenaFroala = (props: NvAthenaFroalaProps) => {
  const {
    containerClassName,
    maxHeight,
    resize,
    ...restProps
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const methods = useFormContext();
  const { formState } = methods || {};
  const hasError = formState?.errors[props.name];

  const froalaStyles = css`
    .fr-wrapper {
      border: ${hasError ? `1px solid ${danger}` : `1px solid ${gray3}`};
    }

    .fr-element {
      resize: ${resize || ResizeType.VERTICAL};
      max-height: ${maxHeight || FROALA_MAX_HEIGHT}px;
      ${hasError ? `
        &:focus {
          outline: none !important;
        }
      ` : ''}
    }
    ${props.required ? `
      .fr-wrapper::after {
        content: '*';
        position: absolute;
        top: 0;
        right: 0;
        width: ${largeSpacing}px;
        height: ${largeSpacing}px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${textMediumFontSize}px;
        color: ${gray3};
        pointer-events: none;
        z-index: 10;
      }
    ` : ''}
  `;

  const froalaConfig = {
    css: getSanitizedStyles([styles, froalaStyles]),
    ...defaultConfig,
    ...restProps,
  };

  const handleBlur = (event?, content?) => {
    props.onBlur?.(event, content);
    setIsFocused(false);
  };

  return (
    <div className={containerClassName}>
      <NvPopover
        enabled={hasError}
        show={hasError && isFocused}
        content={<ErrorPopoverContent message={hasError?.message} />}
        overlayStyles={props.overlayStyles}
        placement='top'
      >
        <NvFroala
          {...froalaConfig}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          ariaLabel={props.ariaLabel}
        />
      </NvPopover>
    </div>
  );
};

export default NvAthenaFroala;
